<template>
  <div>
    <v-row no-gutters>
      <v-col cols="12" sm="4" md="4" lg="3">
        <v-select class="ml-2 mt-2 fiscal-terms-selection" v-model="selectedFiscalTerms" :items="fiscalTermsList"
          :disabled="loading" @change="changeFiscalTerm" :menu-props="{ bottom: true, offsetY: true }" outlined
          item-text="Project(Name)" item-value="Project(Code)" :no-data-text="$t('noData')"
          :label="!Array.isArray(selectedFiscalTerms) ? $t('business.lblFiscalTerms') : $t('lblPleaseSelect')" return-object dense filled></v-select>
      </v-col>
      <v-col cols="12" sm="4" md="4" lg="3">
        <v-select class="ml-2 mt-2 comparedTo-selection" v-model="selectedComparedTo" :items="fiscalTermsList"
          :disabled="loading" @change="changeComparedTo" :menu-props="{ bottom: true, offsetY: true }" outlined
          item-text="Project(Name)" item-value="Project(Code)" :no-data-text="$t('noData')"
          :label="!Array.isArray(selectedComparedTo) ? $t('business.lblComparedTo') : $t('lblPleaseSelect')" return-object dense filled></v-select>
      </v-col>
    </v-row>
    <v-card elevation="0" class="ma-0 pa-2">
      <v-skeleton-loader v-if="loading" type="table-thead, table-tbody"></v-skeleton-loader>
      <LCAGridView v-else :lcaData="consolidatedLcaRes" :key="render"></LCAGridView>
      <v-row no-gutters class="mt-2 d-flex justify-space-between" v-if="updateBusinessPerformancePermission" :disabled="loading">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn v-bind="attrs" v-on="on" small text color="primary"
              v-if="updateBusinessPerformancePermission" @click="selectLCAReport()"
              :disabled="loading">
              {{ $t("business.btnUploadLCAReportByConsolidated") }}
            </a-btn>
          </template>
          <span> {{ $t("business.btnUploadLCAReportByConsolidated") }} </span>
        </v-tooltip>
        <v-tooltip bottom v-if="showDataManagementBtn">
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              v-bind="attrs"
              v-on="on"
              v-if="showDataManagementBtn"
              :disabled="loading"
              small
              text
              color="#888"
              @click="isDataManagementDialogOpen = true"
            >
              <v-icon>mdi-delete</v-icon>
            </a-btn>
          </template>
          <span> {{ $t("btnDelete") }} </span>
        </v-tooltip>
      </v-row>

      <a-dialog v-model="uploadDialog" persistent scrollable max-width="700px">
        <v-card flat>
          <v-toolbar color="#CFDDE6" flat dark height="48px">
            <a-btn color="darkCharcoal" icon large>
              <v-icon>mdi-cloud-upload</v-icon>
            </a-btn>
            <span>{{ $t("business.lblUploadLCAReportByConsolidated") }}</span>
            <v-spacer></v-spacer>
            <CloseIcon @click="closeUploadFiles" />
          </v-toolbar>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" style="padding: 0 !important">
                  <v-form ref="form" v-model="valid" lazy-validation>
                    <upload-file v-if="uploadDialog" :files="uploadingFiles" :icons="fileIcons"
                      :clearList="uploadDialog" v-on:add-files="addUploadingFiles"
                      v-on:remove-file="removeUploadingFile" :accept="file_accept" :hideDetails="false"
                      :chunkUpload="false"></upload-file>
                  </v-form>
                </v-col>
                <v-col cols="12" style="padding: 0 10px !important">
                 {{ $t("business.lblUploadReportNotice") }}
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <span v-if="errorCount > 0" class="error--text" style="
                font-family: 'Noto Sans JP';
                font-style: normal;
                font-weight: 400;
                font-size: 16px;
                line-height: 18px;
              "><small>{{
                $t("documents.invalidFileMsg", { count: errorCount })
              }}</small></span>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <a-btn small v-bind="attrs" :disabled="!valid ||
                  errorCount > 0 ||
                  !Boolean(uploadingFiles.length) ||
                  loading
                  " v-on="on" :class="uploadingFiles.length ? 'primaryDark' : 'secondary'" @click="uploadLCAReport">{{
                    $t("documents.upload") }}</a-btn>
              </template>
              <span>{{ $t("documents.upload") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <a-btn small v-bind="attrs" v-on="on" class="secondary ml-2" @click="closeUploadFiles"
                  :disabled="loading">{{ $t("btnClose") }}</a-btn>
              </template>
              <span>{{ $t("btnClose") }}</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </a-dialog>
      <data-management-dialog
        :isOpen="isDataManagementDialogOpen"
        :consolidatedCompanies="fiscalTermsList"
        @update:isOpen="updateDataManagementDialog" 
        @update:loading="updateLoading" 
        @onDelete="onDeleteLCARecords" 
        @renderTab="handleRenderTab"
      ></data-management-dialog>
      <Loader :loading="loading" :message="LoaderText"></Loader>
      <MessageSheet :showMessage="displayMessage" :message="message" :messageType="messageType"
        @close="displayMessage = false"></MessageSheet>
    </v-card>
  </div>
</template>

<script>
import Vue from "vue";
import UploadFile from "@/components/documents/UploadFile.vue";
import { mapActions, mapState } from "vuex";
import Loader from "@/components/Loader.vue";
import { createUUID, formatBytes } from "@/utils/util";
import { fileIcons, MAX_UPLOAD_SIZE } from "@/utils/constant";
import MessageSheet from "@/components/MessageSheet.vue";
import LCAGridView from "./LCAGridView.vue";
import DataManagementDialog from "./DataManagementDialog.vue";

Vue.prototype.$eventHub = new Vue();
export default {
  name: "AgendaViewGrid",
  components: {
    MessageSheet,
    UploadFile,
    Loader,
    LCAGridView,
    DataManagementDialog
  },
  props: {
    gridData: Array,
    gridHeight: { type: Number, default: window.innerHeight - 195 },
  },
  data() {
    return {
      render: 0,
      companies: [],
      isFileUploaded: false,
      consolidatedCompanies: [],
      fiscalTermsList: [],
      selectedFiscalTerms: [],
      selectedComparedTo: [],
      displayMessage: false,
      isDataManagementDialogOpen: false,
      message: "",
      messageType: "",
      fileType: [],
      file_accept: ["text/plain", ".tsv", ""],
      lastSelectedIndex: null,
      valid: false,
      loading: false,
      fileIcons,
      uploadingFiles: [],
      uploadDialog: false,
      isOpen: false,
      selectedCompanies: [],
      companyList: [],
      pageSettings: { pageSize: 50 },
      isInvalidLCAFile: false,
      dataSource: [],
      wrapSettings: { wrapMode: "Content" },
    };
  },
  watch: {
    currentLCATab() {
      this.render++
    },
  },
  computed: {
    ...mapState({
      lcaFiscalTerms: (state) => state.agenda.lcaFiscalTerms,
      lcaCompanies: (state) => state.agenda.lcaCompanies,
      consolidatedLcaData: (state) => state.agenda.consolidatedLcaData,
      currentLCATab: (state) => state.agenda.currentLCATab
    }),
    updateBusinessPerformancePermission() {
      return this.$security.checkUserAccess("UpdateBusinessPerformance");
    },
    errorCount() {
      let count = 0;
      for (const item of this.uploadingFiles) {
        if (this.checkStatus(item).status === false) {
          count++;
        }
      }
      return count;
    },
    LoaderText() {
      return this.$t("LoaderText");
    },
    consolidatedLcaRes() {
      return this.consolidatedLcaData;
    },
    showDataManagementBtn() {
      return this.updateBusinessPerformancePermission && this.fiscalTermsList.length > 0;
    }
  },

  async mounted() {
    await this.getFiscalTerms();
  },
  methods: {
    ...mapActions({
      getLcaFiscalTerms: "agenda/getLcaFiscalTerms",
      getLCACompanies: "agenda/getLCACompanies",
      getConsolidatedLCAData: "agenda/getConsolidatedLCAData",
      createConsolidatedData: "agenda/createConsolidatedData",
    }),
    async handleRenderTab(){
      this.$emit('renderTab', true)
      this.render++
    },
    async getFiscalTerms(afterDelete) {
      try {
        this.loading = true;
        await this.getLcaFiscalTerms({ report_type: 'lca-650' });
        this.$emit('renderTab', true)
        this.fiscalTermsList = this.lcaFiscalTerms.length > 0 ? this.lcaFiscalTerms?.sort((a,b) => b?.["Project(Code)"].localeCompare(a?.["Project(Code)"])) : [];
        if(afterDelete) {
          this.selectedFiscalTerms = this.fiscalTermsList.length ? this.fiscalTermsList[0] : [];
          this.selectedComparedTo = this.fiscalTermsList.length ? this.fiscalTermsList[0] : [];
        } else {
          this.selectedFiscalTerms = this.fiscalTermsList.length ? this.fiscalTermsList[0] : [];
          this.selectedComparedTo = this.fiscalTermsList.length ? this.fiscalTermsList[0] : [];
          if(!this.isFileUploaded)
          {
            this.selectPreviousYearProject()
          }
        }
        await this.retrieveLCAData(this.selectedFiscalTerms, this.selectedComparedTo)
      } catch (error) {
        this.loading = false;
      } finally {
        this.loading = false;
      }
    },
    selectPreviousYearProject() {
      const currentCode = this.selectedFiscalTerms["Project(Code)"];

      const yearStr = currentCode.slice(3, 7);

      if (!/^\d{4}$/.test(yearStr)) {
        return;
      }
      
      const prevYear = parseInt(yearStr) - 1;
      const prevCode = currentCode.slice(0, 3) + prevYear + currentCode.slice(7);
      
      const match = this.fiscalTermsList.find(
        p => String(p["Project(Code)"]) === String(prevCode)
      );

      if (match) {
        this.selectedComparedTo = match;
      } else {
        this.selectedComparedTo = []
      }
      this.isFileUploaded = false
    },
    showMessageSheet(message, messageType) {
      this.displayMessage = true;
      this.message = message;
      this.messageType = messageType;
    },
    checkStatus(file) {
      const maxSize = MAX_UPLOAD_SIZE ?? 0;
      let filesWithStatus = {
        filename: file.name,
      };
      if (!this.file_accept.includes(file.type)) {
        filesWithStatus.status = false;
        filesWithStatus.message = this.$t("fileErrorMsg", {
          filename: file.name,
        });
      } else if (file.size > maxSize) {
        filesWithStatus.status = false;
        filesWithStatus.message = this.$t("fileSizeErrorMsg", {
          maxsize: formatBytes(maxSize),
        });
      } else {
        filesWithStatus.status = true;
        filesWithStatus.message = "Success";
      }
      return filesWithStatus;
    },
    changeFiscalTerm(newFiscalTerms) {
      this.selectPreviousYearProject()
      this.retrieveLCAData(
        newFiscalTerms,
        this.selectedComparedTo,
      );
    },
    changeComparedTo(newComparedTo) {
      this.retrieveLCAData(
        this.selectedFiscalTerms,
        newComparedTo,
      );
    },
    async retrieveLCAData(
      selectedFiscalTerms,
      selectedComparedTo,
    ) {
      try {
        this.isFileUploaded = true
        this.loading = true;
        let data = {
          project_code: selectedFiscalTerms["Project(Code)"],
          project_name: selectedFiscalTerms["Project(Name)"],
          previous_project_code: selectedComparedTo["Project(Code)"],
          previous_project_name: selectedComparedTo["Project(Name)"],
        };
        await this.getConsolidatedLCAData(data);
      }
      catch (error) {
        this.loading = false;
      } finally {
        this.$nextTick(() => {
          this.$emit('renderTab', true);
          this.render++;
          this.loading = false;
        });
      }
    },
    closeUploadFiles() {
      this.uploadingFiles = [];
      this.uploadDialog = false;
      this.$refs.form.reset();
      this.$refs.form.resetValidation();
    },
    removeUploadingFile(index) {
      this.uploadingFiles.splice(index, 1);
    },
    addUploadingFiles(files) {
      files = Array.from(files);

      this.uploadingFiles.push(...files);
    },
    selectLCAReport() {
      this.uploadDialog = true;
    },
    async uploadLCAReport() {
      if (this.$refs.form.validate()) {
        this.uploadingFiles.forEach((item) => {
          this.fileType.push(
            item.type ||
            item.name.split(".").pop() === "tsv"
              ? "text/tab-separated-values"
              : "")
        })
        this.loading = true;
      }
      try {
        for (let index = 0; index < this.uploadingFiles.length; index++) {
          const item = this.uploadingFiles[index];
          const formData = new FormData();  

          formData.append("file", item);
          formData.append("filename", item.name);
          formData.append("content_type", this.fileType[index]);
          formData.append("documentId", createUUID());
          formData.append("document_type", "lca-650");
          await this.createConsolidatedData(formData);
          this.showMessageSheet(
            this.$t("documents.DocUploadSuccessMessage"),
            "success"
          );
        }
        this.$emit('renderTab', true)
        this.closeUploadFiles();
        await this.getFiscalTerms();
      } catch (error) {
        throw error;
      } finally {
        this.loading = false;
      }
    },
    updateDataManagementDialog(value) {
      this.isDataManagementDialogOpen = value;
    },
    updateLoading(value) {
      this.loading = value;
    },
    async onDeleteLCARecords(value) {
      if(value)
      {
        this.showMessageSheet(
          this.$t("business.msgDeleteLCARecordsSuccessByConsolidated"),
          "success"
        );
        await this.getFiscalTerms(true);
      }
    }
  },
};
</script>

<style scoped>
.v-toolbar /deep/ .v-toolbar__content {
  color: var(--v-darkCharcoal-base);
  font-family: Noto Sans JP;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
}

::v-deep .disablegrid {
  pointer-events: none;
  opacity: 0.9;
}

.fiscal-terms-selection /deep/ .v-text-field__details,
.comparedTo-selection /deep/ .v-text-field__details {
  display: none !important;
}

.fiscal-terms-selection /deep/ .v-input__slot,
.comparedTo-selection /deep/ .v-input__slot {
  margin-bottom: 0px;
  min-height: 45px;
  max-height: 45px;
  background: #fafafa;
  border-radius: 4px;
}

.fiscal-terms-selection /deep/ fieldset,
.comparedTo-selection /deep/ fieldset {
  border-color: rgba(0, 0, 0, 0.12);
}
</style>
